import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { customAuth, headerAuth } from "../standardAuthPatterns";
import {
  defineProduct,
  productVariant,
  type ConnectorDefinition,
} from "../types";
import logo from "./logo.svg";
import { openaiUsage } from "./usage";

export const DEFAULT_OPENAI_BASE_URL = "https://api.openai.com/v1";

const openaiModelsFetch = openAiModelsFetch(
  "Fetch models from OpenAI",
  ({ baseUrl }) => baseUrlEndpoint(baseUrl, DEFAULT_OPENAI_BASE_URL, "/models")
);

const modelsStep = {
  title: "Choose available models (optional)",
  items: [
    {
      kind: "list",
      label: "Available models",
      prefix: MODELS_PREFIX,
      columns: [{ field: "name", placeholder: "gpt-5.6-sol" }],
      addLabel: "Add model",
      fetch: openaiModelsFetch,
      helperText: MODELS_HELPER_TEXT,
    },
  ],
} satisfies ConnectorDefinition["steps"][number];

const usageHint = (configuration: Record<string, string>) =>
  withAllowedModelsHint(
    "Use process.env.OPENAI_BASE_URL as the OpenAI API base URL. Pass a placeholder API key; the proxy injects the real credential from the connector.",
    configuration
  );

const apiKeyVariant = productVariant({
  identifier: "openai",
  authLabel: "API key",
  protocol: "http",
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_OPENAI_BASE_URL),
  auth: (app) =>
    headerAuth([
      {
        name: "Authorization",
        value: app.secret.apiKey,
        scheme: "Bearer",
      },
      {
        name: "OpenAI-Organization",
        value: app.config.organizationId,
      },
    ]),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [OpenAI API keys page](https://platform.openai.com/api-keys) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "OpenAI API key",
          placeholder: "sk-...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "markdown",
          content:
            "If you belong to multiple organizations, you can optionally specify which organization’s quota to use. [Learn more about authentication](https://platform.openai.com/docs/api-reference/authentication).",
        },
        {
          kind: "input",
          label: "Organization ID (optional)",
          placeholder: "org-...",
          target: { kind: "configuration", field: "organizationId" },
        },
        {
          kind: "baseUrl",
          label: "Base URL (optional)",
          placeholder: DEFAULT_OPENAI_BASE_URL,
          defaultValue: DEFAULT_OPENAI_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default OpenAI API endpoint.",
        },
      ],
    },
    modelsStep,
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_OPENAI_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    OPENAI_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_OPENAI_BASE_URL),
  }),
  usageHint,
  usage: openaiUsage(),
});

const federationVariant = productVariant({
  identifier: "openaiFederation",
  authLabel: "Workload identity federation (keyless)",
  protocol: "http",
  defaultUrlPattern: () => baseUrlPattern(undefined, DEFAULT_OPENAI_BASE_URL),
  auth: (app) =>
    customAuth("openaiFederation", {
      issuer: app.issuer ?? "",
      identityProviderId: app.config.identityProviderId,
      serviceAccountId: app.config.serviceAccountId,
    }),
  steps: [
    {
      title: "Configure a workload identity provider",
      items: [
        {
          kind: "markdown",
          content:
            "For OIDC providers outside OpenAI’s documented provider list, contact OpenAI support to confirm compatibility. In [Organization settings → Security → Workload Identity Provider](https://platform.openai.com/settings/organization/security/workload-identity-provider), create an OIDC provider with the issuer URL below. Use standard OIDC discovery.",
        },
        {
          kind: "copyable",
          label: "OIDC issuer URL",
          value: "{OIDC_PROVIDER_URL}",
        },
        {
          kind: "copyable",
          label: "Audience",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
        {
          kind: "markdown",
          content:
            "On the provider’s details page, add a service account mapping for the project and service account you want to use. Match the raw **sub** claim exactly to the value below, and grant only the API permissions you need, including model read access for the connection test and model picker. Workload identity tokens can’t call the Admin API.",
        },
        {
          kind: "copyable",
          label: "Subject (sub)",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
      ],
    },
    {
      title: "Enter your federation details",
      items: [
        {
          kind: "input",
          label: "Identity provider ID",
          required: true,
          target: { kind: "configuration", field: "identityProviderId" },
        },
        {
          kind: "input",
          label: "Service account ID",
          required: true,
          target: { kind: "configuration", field: "serviceAccountId" },
          helperText:
            "Use the service account selected in the provider’s project mapping.",
        },
      ],
    },
    modelsStep,
  ],
  connectionTest: {
    protocol: "http",
    request: () => `${DEFAULT_OPENAI_BASE_URL}/models`,
  },
  publicEnvVars: () => ({ OPENAI_BASE_URL: DEFAULT_OPENAI_BASE_URL }),
  usageHint,
  usage: openaiUsage(),
});

export const openai = defineProduct({
  name: "OpenAI",
  tagLine: "AI models and assistants",
  keywords: "gpt chatgpt llm models inference completions oidc federation",
  logo,
  tags: ["featured", "ai", "engineering"],
  variants: [apiKeyVariant, federationVariant],
});
